import {
  Grid,
  Box,
  Tooltip,
  IconButton,
  Link,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid, GridSelectionModel } from "@mui/x-data-grid";
import { addDays, format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import CTypography from "src/components/CTypography";
import FormikControl from "src/components/formik/FormikControl";
import { REGEX_IS_NUMBER, GENDER, getInvoiceStatusValue } from "src/constants";
import { Utils } from "src/lib/utils";
import { TableCustomer } from "src/types/tableTypes";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ContainedButton, OutlinedButton } from "src/components/button-group";
import { AppDispatch } from "src/store";
import {
  selectItems,
  selectServiceItemPrices,
  selectServices,
  selectTotalServices,
  selectTotalItems,
  selectLocations,
} from "src/slices/configuration/selectors";
import {
  IItem,
  IService,
  IServiceItemPrice,
} from "src/slices/configuration/types";
import {
  fetchAllServices,
  fetchAllItems,
  fetchItems,
  fetchServiceItemPrices,
  fetchServices,
  fetchLocations,
} from "src/slices/configuration/thunks";
import { fetchCustomerAddresses } from "src/slices/customers/thunks";
import { ICustomerAddress } from "src/slices/customers/types";
import { selectCustomerAddresses } from "src/slices/customers/selectors";
import { emptyCustomerAddressesState } from "src/slices/customers";
import { createUpdateOrder } from "src/slices/orders/thunks";
import CKeyValueList from "src/components/CKeyValueList";
import InvoiceTemplate from "../invoice-template";
import { OrderDetails } from "src/slices/orders/types";
import { selectCreateddOrders } from "src/slices/orders/selectors";
import FileDropzone from "src/components/dropzone";
import { fetchUsers } from "src/slices/users/thunk";
import { selectUsers } from "src/slices/users/selectors";

export interface IformInitialValue {
  invoiceNumber: string;
  customerFullName: string;
  gender: string;
  dateOfBirth: Date;
  contactNumber: string;
  alternateNumber: string;
  emailAddress: string;
  address: string;
  addressLocation: string;
  pickupAddress: string;
  pickupLocation: string;
  deliveryAddress: string;
  deliveryLocation: string;
  dueDate: Date;
  pickupDate: Date;
  servicesCustomerWants: string;
  servicesForItems: string;
  estimatedNumberOfItems: string;
  specialInstruction: string;
  orderStatus: string;
  branchId: number | string;
  createIvpInvoice: boolean;
  tag_code?: string;
  paymentReceivedDate: Date;
  paymentMode: string;
  paymentReceivedBy: string | number | null;
  paymentProofAttachment: File[];
  orderCategory: string;
  customerType: string | null;
  cancelReason: string | null;
  isRecurring: boolean;
}

export function CreateAndUpdateOrder() {
  const dispatch = useDispatch<AppDispatch>();
  const users = useSelector(selectUsers);

  const paymentsModeArr = [
    { id: 1, name: "Cash" },
    { id: 2, name: "Online" },
    // { id: 3, name: "POS" },
  ];

  const [files, setFiles] = useState([]);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const navigate = useNavigate();

  const formikRef = useRef(null);
  const { state } = useLocation();

  const serviceItemPricing: IServiceItemPrice[] = useSelector(
    selectServiceItemPrices
  );
  const customerAddresses: ICustomerAddress[] = useSelector(
    selectCustomerAddresses
  );

  const allServices: IService[] = useSelector(selectTotalServices);
  const allItems: IItem[] = useSelector(selectTotalItems);

  const allActiveServices: IService[] = useSelector(selectServices);
  const allActiveItems: IItem[] = useSelector(selectItems);
  const createdOrder: OrderDetails | null = useSelector(selectCreateddOrders);
  const locations = useSelector(selectLocations);

  // Services
  const [columns] = useState([
    { field: "services", headerName: "Services", width: 300, editable: false },
    { field: "items", headerName: "Items", width: 300, editable: false },
    {
      field: "estimatedItems",
      headerName: "Estimated Items",
      width: 300,
      editable: false,
    },
    {
      field: "price",
      headerName: "Price",
      width: 300,
      editable: false,
    },
  ]);

  const [rows, setRows] = useState([]);
  const [servicesSelectionModel, setServicesSelectionModel] =
    useState<GridSelectionModel>([]);

  const [addressRows, setAddressRows] = useState([]);
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);

  const [showCancelReason, setShowCancelReason] = useState(false);

  // Click Events
  const onClickSubmit = async (values: IformInitialValue) => {
    // console.log("CLICKED HERE");

    let order = {};
    if (state && state.isOrderUpdate) {
      order = {
        orderId: state.id,
        invoicePlaneRefId: values.invoiceNumber,
        customerId: state.customerId,
        pickupAddressGoogleLocation: values.pickupLocation ?? "",
        deliveryAddress: values.deliveryAddress,
        deliveryAddressGoogleLocation: values.deliveryLocation ?? "",
        pickupAddress: values.pickupAddress,
        dueDate: format(values.dueDate, "yyyy-MM-dd"),
        pickUpDate: format(values.pickupDate, "yyyy-MM-dd"),
        orderDate: new Date(Date.now()).toISOString(),
        orderStatus: values.orderStatus,
        cancelReason: values.cancelReason,
        orderCategory: values.orderCategory,
        isCancelled: null,
        itemAndServiceId: rows.map((service) => ({
          serviceId: service.serviceId,
          itemId: service.itemId,
          estimatedNumberOfItems: parseInt(service.estimatedItems),
        })),
        specialInstruction: values.specialInstruction,
        // branchId: state?.locationId || "",
        branchId: values?.branchId,
        createIvpInvoice: state.createIvpInvoice || false,

        // payment details
        // only add orderPayment if paymentMode is provided
        ...(values.paymentMode
          ? {
              orderPayment: {
                paymentMode: values.paymentMode,
                paymentDate: values.paymentReceivedDate,
                receivedBy:
                  values.paymentMode === "Cash"
                    ? values.paymentReceivedBy
                    : null,
                paymentProofFile: files ?? "",
              },
            }
          : null),
      };
    } else {
      order = {
        orderId: null,
        invoicePlaneRefId: values.invoiceNumber,
        customerId: state.id,
        pickupAddressGoogleLocation: values.pickupLocation ?? "",
        deliveryAddress: values.deliveryAddress,
        deliveryAddressGoogleLocation: values.deliveryLocation ?? "",
        pickupAddress: values.pickupAddress,
        dueDate: format(values.dueDate, "yyyy-MM-dd"),
        pickUpDate: format(values.pickupDate, "yyyy-MM-dd"),
        orderDate: new Date(Date.now()).toISOString(),
        orderCategory: values?.orderCategory,
        orderStatus: "1",
        cancelReason: null,
        isCancelled: null,
        itemAndServiceId: rows.map((service) => ({
          serviceId: service.serviceId,
          itemId: service.itemId,
          estimatedNumberOfItems: parseInt(service.estimatedItems),
        })),
        specialInstruction: values.specialInstruction,
        branchId: values?.branchId,
        createIvpInvoice: values.createIvpInvoice,
      };
    }

    dispatch(createUpdateOrder(order)).then((result: any) => {
      if (!result.payload?.error) {
        setOpenInvoiceModal(true);
        // navigate(RoutePaths.ORDER_LISTING);
      }
    });
  };

  const onClickAddService = (values: IformInitialValue) => {
    if (!REGEX_IS_NUMBER.test(values.estimatedNumberOfItems)) return;
    if (values.servicesCustomerWants.length === 0) return;
    if (values.estimatedNumberOfItems.length === 0) return;
    if (values.servicesForItems.length === 0) return;

    setRows((rows) => [
      ...rows,
      {
        id: rows.length,
        serviceId: values.servicesCustomerWants,
        itemId: values.servicesForItems,
        services: allActiveServices.filter(
          (service) => service.id === values.servicesCustomerWants
        )[0].name,
        items: allActiveItems.filter(
          (item) => item.id === values.servicesForItems
        )[0].name,
        estimatedItems: values.estimatedNumberOfItems,
        price:
          parseInt(values.estimatedNumberOfItems) *
          parseInt(
            serviceItemPricing.filter(
              (mapping) =>
                mapping.serviceId === values.servicesCustomerWants &&
                mapping.itemId === values.servicesForItems
            )[0]?.price
          ),
      },
    ]);
    formikRef.current.setFieldValue("servicesCustomerWants", "");
    formikRef.current.setFieldValue("servicesForItems", "");
    formikRef.current.setFieldValue("estimatedNumberOfItems", "");
  };

  const onClickRemoveService = () => {
    setRows(
      rows.filter((service) => !servicesSelectionModel.includes(service.id))
    );
  };

  // Inital Values
  const getDueDateInitDate = (pickupDate?: Date): Date => {
    const currentDate = pickupDate || new Date(Date.now());
    let newDate = addDays(currentDate, 3);
    const isSunday = Utils.isSundayBetween(
      currentDate,
      addDays(currentDate, 3)
    );
    if (isSunday) {
      newDate = addDays(currentDate, 4);
    }
    return newDate;
  };
  const formInitialValue: IformInitialValue = {
    invoiceNumber: "",
    customerFullName: "",
    gender: "",
    dateOfBirth: new Date(Date.now()),
    contactNumber: "",
    alternateNumber: "",
    emailAddress: "",
    address: "",
    addressLocation: "",
    pickupAddress: "",
    pickupLocation: "",
    deliveryAddress: "",
    deliveryLocation: "",
    dueDate: getDueDateInitDate(),
    pickupDate: new Date(Date.now()),
    servicesCustomerWants: "",
    servicesForItems: "",
    estimatedNumberOfItems: "",
    specialInstruction: "",
    orderStatus: "1",
    branchId: "",
    createIvpInvoice: false,
    tag_code: "",
    // payment details
    paymentReceivedDate: new Date(Date.now()),
    paymentMode: "",
    paymentReceivedBy: "",
    paymentProofAttachment: [],
    orderCategory: "",
    customerType: "",
    cancelReason: null,
    isRecurring: false,
  };
  const validationSchemaModel = Yup.object().shape({
    invoiceNumber: Yup.string().test(
      "input type restriction",
      "Only numbers are allowed",
      (value) => {
        return (
          REGEX_IS_NUMBER.test(value) || value === "" || value === undefined
        );
      }
    ),
    // servicesCustomerWants: Yup.string().test(
    //   "required Service",
    //   "Please add at least one service",
    //   (value) => rows.length > 0
    // ),
    branchId: Yup.number()
      .required("Branch is required")
      .min(1, "Branch is required"),
    dueDate: Yup.date().test(
      "greaterThanPickup",
      "Due date must be greater than or equal to pickup date",
      function (dueDate) {
        return dueDate?.getTime() >= this.parent.pickupDate?.getTime();
      }
    ),
    orderCategory: Yup.string().nullable().required("Order Type is required"),
    orderStatus: Yup.string().required("Order Status is required"),
    cancelReason: Yup.string()
      .nullable() // Allow null when not required (matches backend)
      .when("orderStatus", {
        is: "8", // Only when orderStatus is "8" (Cancelled)
        then: (schema) =>
          schema.required("Reason for cancellation is required"),
        otherwise: (schema) => schema.notRequired(), // Optional otherwise
      }),
  });

  useEffect(() => {
    if (state) {
      // console.log(state, "state in create order page");
      if (!state.isOrderUpdate) {
        // populate values
        const customerDetails: TableCustomer = state;
        // console.log("customerDetails here", customerDetails);

        formikRef.current.setFieldValue(
          "customerFullName",
          customerDetails.customerName
        );
        formikRef.current.setFieldValue(
          "customerType",
          customerDetails.customerType
        );
        formikRef.current.setFieldValue(
          "isRecurring",
          customerDetails.isRecurring
        );
        formikRef.current.setFieldValue(
          "gender",
          customerDetails.gender === "" ? "" : GENDER[customerDetails.gender]
        );
        formikRef.current.setFieldValue("emailAddress", customerDetails.email);
        formikRef.current.setFieldValue(
          "contactNumber",
          customerDetails.primaryNumber
        );
        formikRef.current.setFieldValue(
          "alternateNumber",
          customerDetails.secondaryNumber ?? ""
        );
        formikRef.current.setFieldValue(
          "dateOfBirth",
          customerDetails.dateOfBirth ?? new Date(Date.now())
        );
        formikRef.current.setFieldValue(
          "branchId",
          customerDetails?.locationName
        );
        // formikRef.current.setFieldValue("tag_code", "");

        // get data from APIs
        dispatch(fetchCustomerAddresses(customerDetails.id));
        dispatch(fetchAllServices());
        dispatch(fetchAllItems());
        dispatch(fetchServices());
        dispatch(fetchItems());
      } else {
        // populate values
        const customerAndOrderDetails = state;
        // console.log("customerAndOrderDetails here", customerAndOrderDetails);

        if (customerAndOrderDetails?.statusId === "8") {
          setShowCancelReason(true);
        } else {
          setShowCancelReason(false);
        }

        formikRef.current.setFieldValue(
          "customerFullName",
          customerAndOrderDetails.customerName
        );
        formikRef.current.setFieldValue(
          "customerType",
          customerAndOrderDetails.customerType
        );
        formikRef.current.setFieldValue(
          "gender",
          customerAndOrderDetails.gender === ""
            ? ""
            : GENDER[customerAndOrderDetails.customerGender]
        );
        formikRef.current.setFieldValue(
          "isRecurring",
          customerAndOrderDetails.customerIsRecurring
        );
        formikRef.current.setFieldValue(
          "emailAddress",
          customerAndOrderDetails.customerEmail
        );
        formikRef.current.setFieldValue(
          "contactNumber",
          customerAndOrderDetails.customerPrimaryNumber
        );
        formikRef.current.setFieldValue(
          "alternateNumber",
          customerAndOrderDetails.customerSecondaryNumber ?? ""
        );
        formikRef.current.setFieldValue(
          "dateOfBirth",
          customerAndOrderDetails.customerDOB ?? new Date(Date.now())
        );
        formikRef.current.setFieldValue(
          "pickupDate",
          new Date(customerAndOrderDetails.pickUpDate)
        );
        formikRef.current.setFieldValue(
          "dueDate",
          new Date(customerAndOrderDetails.dueDate)
        );
        formikRef.current.setFieldValue(
          "specialInstruction",
          customerAndOrderDetails?.specialInstructions ?? ""
        );

        formikRef.current.setFieldValue(
          "invoiceNumber",
          customerAndOrderDetails?.invoicePlaneRefId ?? ""
        );

        formikRef.current.setFieldValue(
          "orderStatus",
          customerAndOrderDetails?.statusId
        );

        if (customerAndOrderDetails?.statusId === "8") {
          formikRef.current.setFieldValue(
            "cancelReason",
            customerAndOrderDetails?.cancelReason ?? null
          );
        }

        formikRef.current.setFieldValue(
          "orderCategory",
          customerAndOrderDetails.orderCategory ?? ""
        );
        formikRef.current.setFieldValue(
          "branchId",
          customerAndOrderDetails?.locationId
        );

        formikRef.current.setFieldValue(
          "createIvpInvoice",
          customerAndOrderDetails?.createIvpInvoice
        );
        // get data from APIs
        dispatch(fetchCustomerAddresses(customerAndOrderDetails.customerId));
        dispatch(fetchAllServices());
        dispatch(fetchAllItems());
        dispatch(fetchServices());
        dispatch(fetchItems());
        // dispatch(getTagCode(customerAndOrderDetails.id));

        // console.log(customerAndOrderDetails, "customerAndOrderDetails");
      }
      dispatch(fetchServiceItemPrices());
    }
    // eslint-disable-next-line
  }, [state]);

  useEffect(() => {
    if (state && state.isOrderUpdate) {
      formikRef.current.setFieldValue("tag_code", state.tag_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (
      allItems?.length &&
      allServices?.length &&
      state &&
      state.isOrderUpdate
    ) {
      const servicesList = state.servicesItems?.map((service, index) => {
        const serviceId = allServices.filter(
          (ser) => ser.name === service.service
        )[0].id;
        const itemId = allItems.filter((item) => item.name === service.item)[0]
          .id;
        let price =
          parseInt(service.estimatedNumOfItems) * parseInt(service.price);
        price = isNaN(price) ? null : price;
        return {
          id: index,
          serviceId: serviceId,
          items: service.item,
          services: service.service,
          estimatedItems: service.estimatedNumOfItems,
          itemId: itemId,
          price: price,
        };
      });
      setRows(servicesList || []);
    }
    // eslint-disable-next-line
  }, [allServices, allItems, state]);

  // onChange Events
  useEffect(() => {
    if (state && !state.isOrderUpdate) {
      if (addressRows?.length > 0) {
        formikRef.current.setFieldValue(
          "pickupAddress",
          addressRows[0].address
        );
        formikRef.current.setFieldValue(
          "pickupLocation",
          addressRows[0].googleLocation ?? ""
        );
        formikRef.current.setFieldValue(
          "deliveryAddress",
          addressRows[0].address
        );
        formikRef.current.setFieldValue(
          "deliveryLocation",
          addressRows[0].googleLocation ?? ""
        );
      }
    } else if (state && state.isOrderUpdate) {
      formikRef.current.setFieldValue("pickupAddress", state.pickupAddress);
      formikRef.current.setFieldValue(
        "pickupLocation",
        state.pickupAddressGoogleLocation
      );
      formikRef.current.setFieldValue("deliveryAddress", state.deliveryAddress);
      formikRef.current.setFieldValue(
        "deliveryLocation",
        state.deliveryAddressGoogleLocation
      );
    }
  }, [addressRows, state]);

  useEffect(() => {
    if (customerAddresses) {
      setAddressRows(customerAddresses);
    }
    return () => {
      dispatch(emptyCustomerAddressesState());
    };
    // eslint-disable-next-line
  }, [customerAddresses]);

  useEffect(() => {
    dispatch(fetchLocations());
    // eslint-disable-next-line
  }, []);

  return (
    <Grid container spacing={3}>
      <InvoiceTemplate
        order_created={createdOrder}
        open={openInvoiceModal}
        handleClose={() => setOpenInvoiceModal(false)}
      />
      <Grid item xs={12}>
        <Formik
          initialValues={formInitialValue}
          validationSchema={validationSchemaModel}
          innerRef={formikRef}
          onSubmit={(values) => {
            onClickSubmit(values);
          }}
        >
          {({
            values,
            touched,
            errors,
            setFieldValue,
            getFieldProps,
            isValid,
          }) => (
            <>
              {/* {console.log("Form errors:", errors)} */}
              {/* {console.log("Form isValid:", isValid)} */}
              <Form id="CreateOrder">
                {/* Personal Information */}
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Personal Information
                    </CTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="InputField"
                      required
                      disabled
                      name="customerFullName"
                      label={"Customer Full Name"}
                      error={Boolean(
                        touched.customerFullName && errors.customerFullName
                      )}
                      helperText={
                        touched.customerFullName && errors.customerFullName
                      }
                      {...getFieldProps("customerFullName")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="SelectField"
                      name="gender"
                      label="Gender"
                      disabled
                      options={[
                        {
                          label: "Male",
                          value: "Male",
                        },
                        {
                          label: "Female",
                          value: "Female",
                        },
                      ]}
                      error={Boolean(touched.gender && errors.gender)}
                      helperText={touched.gender && errors.gender}
                      {...getFieldProps("gender")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="CalendarTime"
                      label="Date of Birth"
                      dateOrTimeOnly="date"
                      name="dateOfBirth"
                      disabled
                      error={Boolean(touched.dateOfBirth && errors.dateOfBirth)}
                      helperText={touched.dateOfBirth && errors.dateOfBirth}
                      {...getFieldProps("dateOfBirth")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="SelectField"
                      name="customerType"
                      label="Customer Type"
                      disabled
                      options={[
                        { label: "Regular", value: "Regular" },
                        { label: "Package", value: "Package" },
                      ]}
                      {...getFieldProps("customerType")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isRecurring}
                          name="isRecurring"
                          disabled
                          {...getFieldProps("isRecurring")}
                        />
                      }
                      label="Is Walk-in Customer?"
                    />
                  </Grid>
                </Grid>

                {/* Contact Information */}
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Contact Information
                    </CTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="InputField"
                      required
                      name="contactNumber"
                      label="Contact Number"
                      disabled
                      error={Boolean(
                        touched.contactNumber && errors.contactNumber
                      )}
                      helperText={touched.contactNumber && errors.contactNumber}
                      {...getFieldProps("contactNumber")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="InputField"
                      name="alternateNumber"
                      label="Alternate Number"
                      disabled
                      error={Boolean(
                        touched.alternateNumber && errors.alternateNumber
                      )}
                      helperText={
                        touched.alternateNumber && errors.alternateNumber
                      }
                      {...getFieldProps("alternateNumber")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="InputField"
                      name="emailAddress"
                      label="Email Address"
                      disabled
                      error={Boolean(
                        touched.emailAddress && errors.emailAddress
                      )}
                      helperText={touched.emailAddress && errors.emailAddress}
                      {...getFieldProps("emailAddress")}
                    />
                  </Grid>
                </Grid>

                {/* Branch Imformation */}
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Branch Information
                    </CTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="SelectField"
                      name="branchId"
                      label="Branch Name"
                      options={locations.map((service) => ({
                        value: service.id,
                        label: service.name,
                      }))}
                      handleChange={(e: any) => {
                        setFieldValue("branchId", e.target.value);
                      }}
                      error={Boolean(touched.branchId && errors.branchId)}
                      helperText={touched.branchId && errors.branchId}
                      {...getFieldProps("branchId")}
                    />
                  </Grid>
                </Grid>

                {/* Invoice */}
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">Invoice</CTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="InputField"
                      name="invoiceNumber"
                      label={"Invoice#"}
                      error={Boolean(
                        touched.invoiceNumber && errors.invoiceNumber
                      )}
                      helperText={touched.invoiceNumber && errors.invoiceNumber}
                      {...getFieldProps("invoiceNumber")}
                    />
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="Checkbox"
                    name="createIvpInvoice"
                    label={"Create Invoice"}
                    error={Boolean(
                      touched.createIvpInvoice && errors.createIvpInvoice
                    )}
                    helperText={touched.createIvpInvoice && errors.createIvpInvoice}
                    {...getFieldProps("createIvpInvoice")}
                  />
                </Grid> */}
                </Grid>

                {/* Pickup and Delivery Information */}
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Pickup and Delivery Information
                    </CTypography>
                  </Grid>

                  {/* Pickup */}
                  <Grid item xs={12} sm={8}>
                    <FormikControl
                      control="SelectField"
                      name="pickupAddress"
                      label="Pickup Address"
                      options={addressRows.map((addressData) => ({
                        value: addressData.address,
                        label: addressData.address,
                      }))}
                      handleChange={(e: any) => {
                        setFieldValue("pickupAddress", e.target.value);
                        setFieldValue(
                          "pickupLocation",
                          addressRows.filter(
                            (addressData) =>
                              addressData.address === e.target.value
                          )[0].googleLocation ?? ""
                        );
                      }}
                      error={Boolean(
                        touched.pickupAddress && errors.pickupAddress
                      )}
                      helperText={touched.pickupAddress && errors.pickupAddress}
                      {...getFieldProps("pickupAddress")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Grid container spacing={1}>
                      <Grid item xs>
                        <FormikControl
                          control="InputField"
                          name="pickupLocation"
                          label="Pickup Google Location"
                          disabled
                          error={Boolean(
                            touched.pickupLocation && errors.pickupLocation
                          )}
                          helperText={
                            touched.pickupLocation && errors.pickupLocation
                          }
                          {...getFieldProps("pickupLocation")}
                        />
                      </Grid>
                      <Grid item>
                        <Tooltip title="Copy">
                          <IconButton
                            color="inherit"
                            disabled={values.pickupLocation === ""}
                            onClick={() =>
                              navigator.clipboard.writeText(
                                values.pickupLocation
                              )
                            }
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Delivery */}
                  <Grid item xs={12} sm={8}>
                    <FormikControl
                      control="SelectField"
                      name="deliveryAddress"
                      label="Delivery Address"
                      options={addressRows.map((addressData) => ({
                        value: addressData.address,
                        label: addressData.address,
                      }))}
                      handleChange={(e: any) => {
                        setFieldValue("deliveryAddress", e.target.value);
                        setFieldValue(
                          "deliveryLocation",
                          addressRows.filter(
                            (addressData) =>
                              addressData.address === e.target.value
                          )[0].googleLocation ?? ""
                        );
                      }}
                      error={Boolean(
                        touched.deliveryAddress && errors.deliveryAddress
                      )}
                      helperText={
                        touched.deliveryAddress && errors.deliveryAddress
                      }
                      {...getFieldProps("deliveryAddress")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Grid container spacing={1}>
                      <Grid item xs>
                        <FormikControl
                          control="InputField"
                          name="deliveryLocation"
                          label="Delivery Google Location"
                          disabled
                          error={Boolean(
                            touched.deliveryLocation && errors.deliveryLocation
                          )}
                          helperText={
                            touched.deliveryLocation && errors.deliveryLocation
                          }
                          {...getFieldProps("deliveryLocation")}
                        />
                      </Grid>
                      <Grid item>
                        <Tooltip title="Copy">
                          <IconButton
                            color="inherit"
                            disabled={values.deliveryLocation === ""}
                            onClick={() =>
                              navigator.clipboard.writeText(
                                values.deliveryLocation
                              )
                            }
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="CalendarTime"
                      label="Pickup Date"
                      dateOrTimeOnly="date"
                      name="pickupDate"
                      handleChange={(newValue) => {
                        setFieldValue("pickupDate", newValue);
                        setFieldValue("dueDate", getDueDateInitDate(newValue));
                      }}
                      error={Boolean(touched.pickupDate && errors.pickupDate)}
                      helperText={touched.pickupDate && errors.pickupDate}
                      {...getFieldProps("pickupDate")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="CalendarTime"
                      label="Due Date"
                      dateOrTimeOnly="date"
                      name="dueDate"
                      error={Boolean(touched.dueDate && errors.dueDate)}
                      helperText={touched.dueDate && errors.dueDate}
                      {...getFieldProps("dueDate")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="InputField"
                      name="specialInstruction"
                      label="Special Instruction"
                      error={Boolean(
                        touched.specialInstruction && errors.specialInstruction
                      )}
                      helperText={
                        touched.specialInstruction && errors.specialInstruction
                      }
                      {...getFieldProps("specialInstruction")}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">Order Type</CTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="SelectField"
                      name="orderCategory"
                      label="Order Type"
                      options={[
                        { label: "Regular", value: "Regular" },
                        { label: "Package", value: "Package" },
                      ]}
                      error={Boolean(
                        touched.orderCategory && errors.orderCategory
                      )}
                      helperText={touched.orderCategory && errors.orderCategory}
                      required
                    />
                  </Grid>
                </Grid>

                {/* Order Status */}
                {state && state.isOrderUpdate && (
                  <Grid container spacing={1} pb={3}>
                    <Grid item xs={12}>
                      <CTypography fontWeight="bold">Order Status</CTypography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormikControl
                        control="SelectField"
                        name="orderStatus"
                        label="Order Status"
                        options={[
                          {
                            label: "New Order",
                            value: "1",
                          },
                          {
                            label: "Ready for Pickup",
                            value: "2",
                          },
                          {
                            label: "In Progress",
                            value: "3",
                          },
                          {
                            label: "Ready for Delivery",
                            value: "4",
                          },
                          {
                            label: "Delivered",
                            value: "5",
                          },
                          {
                            label: "Pickup Failed",
                            value: "6",
                          },
                          {
                            label: "Delivery Failed",
                            value: "7",
                          },
                          {
                            label: "Cancelled",
                            value: "8",
                          },
                        ]}
                        handleChange={(e: any) => {
                          setFieldValue("orderStatus", e.target.value);
                          setShowCancelReason(e.target.value === "8");
                        }}
                        error={Boolean(
                          touched.orderStatus && errors.orderStatus
                        )}
                        helperText={touched.orderStatus && errors.orderStatus}
                        {...getFieldProps("orderStatus")}
                      />
                    </Grid>
                  </Grid>
                )}

                {showCancelReason && (
                  <Grid container spacing={1} pb={3}>
                    <Grid item xs={12}>
                      <CTypography fontWeight="bold">Cancel Reason</CTypography>
                    </Grid>{" "}
                    <Grid item xs={12} sm={6} md={6}>
                      <FormikControl
                        control="InputField"
                        name="cancelReason"
                        label="Reason for Cancellation"
                        required
                        error={Boolean(
                          touched.cancelReason && errors.cancelReason
                        )}
                        helperText={touched.cancelReason && errors.cancelReason}
                        {...getFieldProps("cancelReason")}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Tag Code (visible only in update mode) */}
                {state?.isOrderUpdate && (
                  <Grid container spacing={1} pb={3}>
                    <Grid item xs={12}>
                      <CTypography fontWeight="bold">Tag Code</CTypography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="InputField"
                        name="tag_code"
                        label="Tag Code"
                        placeholder="Tag Code"
                        required
                        disabled
                        error={Boolean(touched.tag_code && errors.tag_code)}
                        helperText={touched.tag_code && errors.tag_code}
                        {...getFieldProps("tag_code")}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Order Information */}
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Order Information
                    </CTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="SelectField"
                      name="servicesCustomerWants"
                      label="Services Customer Wants"
                      options={allActiveServices.map((service) => ({
                        value: service.id,
                        label: service.name,
                      }))}
                      handleChange={(e: any) => {
                        setFieldValue("servicesCustomerWants", e.target.value);
                        setFieldValue("servicesForItems", "");
                      }}
                      error={Boolean(
                        touched.servicesCustomerWants &&
                          errors.servicesCustomerWants
                      )}
                      helperText={
                        touched.servicesCustomerWants &&
                        errors.servicesCustomerWants
                      }
                      {...getFieldProps("servicesCustomerWants")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="SelectField"
                      name="servicesForItems"
                      label="Items"
                      options={serviceItemPricing
                        .filter(
                          (mapping) =>
                            mapping.serviceId === values.servicesCustomerWants
                        )
                        .map((filteredItems) => ({
                          value: filteredItems.itemId,
                          label: filteredItems.itemName,
                        }))}
                      error={Boolean(
                        touched.servicesForItems && errors.servicesForItems
                      )}
                      helperText={
                        touched.servicesForItems && errors.servicesForItems
                      }
                      {...getFieldProps("servicesForItems")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="InputField"
                      name="estimatedNumberOfItems"
                      label="Estimated Number of Items*"
                      error={Boolean(
                        touched.estimatedNumberOfItems &&
                          errors.estimatedNumberOfItems
                      )}
                      helperText={
                        touched.estimatedNumberOfItems &&
                        errors.estimatedNumberOfItems
                      }
                      {...getFieldProps("estimatedNumberOfItems")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item>
                        <OutlinedButton
                          size="small"
                          onClick={() => {
                            onClickAddService(values);
                          }}
                        >
                          Add Service
                        </OutlinedButton>
                      </Grid>
                      <Grid item>
                        <OutlinedButton
                          size="small"
                          disabled={servicesSelectionModel?.length <= 0}
                          onClick={() => {
                            onClickRemoveService();
                          }}
                        >
                          Remove Service
                        </OutlinedButton>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={7} md={8} lg={9}>
                    <Box sx={{ height: 300, width: 1 }}>
                      <DataGrid
                        disableColumnFilter
                        disableColumnSelector
                        disableDensitySelector
                        hideFooter
                        checkboxSelection
                        onSelectionModelChange={(newSelectionModel) => {
                          setServicesSelectionModel(newSelectionModel);
                        }}
                        selectionModel={servicesSelectionModel}
                        columns={columns}
                        rows={rows || []}
                      />
                    </Box>
                  </Grid>
                  {state.revenueDetails && (
                    <Grid item xs={12} sm={5} md={4} lg={3}>
                      {state.revenueDetails?.map((revenue) => (
                        <CKeyValueList
                          labelWidth={150}
                          size="small"
                          list={[
                            {
                              key: "Invoice Date",
                              value: revenue.invoice_date_created,
                            },
                            {
                              key: "Due Date",
                              value: revenue.invoice_date_due,
                            },
                            {
                              key: "Invoice URL",
                              value: (
                                <Link
                                  href={revenue.invoice_url_key}
                                  target="_blank"
                                >
                                  <PictureAsPdfIcon />
                                </Link>
                              ),
                            },
                            {
                              key: "Sub-Total",
                              value: revenue.invoice_item_subtotal,
                            },
                            {
                              key: "Discount",
                              value: revenue.invoice_discount_amount,
                            },
                            {
                              key: "Total",
                              value: revenue.invoice_total,
                            },
                            {
                              key: "Paid",
                              value: revenue.invoice_paid,
                            },
                            {
                              key: "Balance",
                              value: revenue.invoice_balance,
                            },
                            {
                              key: "Payment Status",
                              value: getInvoiceStatusValue(
                                parseInt(revenue.invoice_status_id)
                              ),
                            },
                            {
                              key: "Payment Date",
                              value: revenue.payment_date,
                            },
                            {
                              key: "Payment Method",
                              value: revenue.payment_method_name,
                            },
                          ]}
                        />
                      ))}
                    </Grid>
                  )}
                </Grid>

                {state && state.isOrderUpdate ? (
                  <Grid container spacing={1} pb={3}>
                    <Grid item xs={12}>
                      <CTypography fontWeight="bold">
                        Order Payment Proof
                      </CTypography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormikControl
                        control="SelectField"
                        name="paymentMode"
                        label="Payment Mode"
                        options={paymentsModeArr.map((mode) => ({
                          value: mode.name,
                          label: mode.name,
                        }))}
                        handleChange={(e: any) => {
                          setFieldValue("paymentMode", e.target.value);
                        }}
                        error={Boolean(
                          touched.paymentMode && errors.paymentMode
                        )}
                        helperText={touched.paymentMode && errors.paymentMode}
                        {...getFieldProps("paymentMode")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormikControl
                        control="CalendarTime"
                        label="Payment Received Date"
                        dateOrTimeOnly="date"
                        name="paymentReceivedDate"
                        error={Boolean(
                          touched.paymentReceivedDate &&
                            errors.paymentReceivedDate
                        )}
                        helperText={
                          touched.paymentReceivedDate &&
                          errors.paymentReceivedDate
                        }
                        {...getFieldProps("paymentReceivedDate")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12}>
                      <FormikControl
                        control="SelectField"
                        name="paymentReceivedBy"
                        label="Payment Received By"
                        disabled={getFieldProps("paymentMode").value !== "Cash"}
                        options={[
                          { value: "", label: "Select User" },
                          ...users.map((user) => ({
                            value: user.id,
                            label: `${user.firstName} ${user.lastName}`,
                          })),
                        ]}
                        handleChange={(e: any) => {
                          const value =
                            e.target.value === null ? "" : e.target.value;
                          setFieldValue("paymentReceivedBy", value);
                        }}
                        error={Boolean(
                          touched.paymentReceivedBy && errors.paymentReceivedBy
                        )}
                        helperText={
                          touched.paymentReceivedBy && errors.paymentReceivedBy
                        }
                        {...getFieldProps("paymentReceivedBy")}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FileDropzone
                        acceptedFileTypes={{
                          "image/jpg": [],
                          "image/jpeg": [],
                          "image/png": [],
                          "application/pdf": [],
                        }}
                        onSelectedFilesChange={(files) => {
                          setFiles(files);
                        }}
                        isDisabled={
                          getFieldProps("paymentMode").value !== "Online"
                            ? true
                            : false
                        }
                      />
                    </Grid>
                  </Grid>
                ) : null}

                {/* SUBMIT FORM */}
                <Grid container spacing={1} sx={{ mb: 20 }}>
                  <Grid item>
                    <ContainedButton type="submit">SUBMIT</ContainedButton>
                  </Grid>
                  <Grid item>
                    <OutlinedButton
                      onClick={() => {
                        navigate(-1);
                      }}
                    >
                      CANCEL
                    </OutlinedButton>
                  </Grid>
                  <Grid item>
                    <OutlinedButton
                      onClick={() => {
                        setOpenInvoiceModal(true);
                      }}
                    >
                      Generate Invoice
                    </OutlinedButton>
                  </Grid>
                </Grid>
              </Form>
            </>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}
